import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  BudgetQuotaResult,
  DictItemRow,
  DictItemSaveRequest,
  DictRow,
  DictSaveRequest,
  InquiryCreateRequest,
  InquiryDetail,
  InquiryRow,
  InterfaceLogQuery,
  InterfaceLogRow,
  QuoteCreateRequest,
  SplitOrderRequest,
  SplitOrderResult,
  WaveAssignRequest,
  WaveOrderRow,
  WaveRow
} from '../types/opsExtras';
import type { PageResult } from '../types/result';

/**
 * 运营补充能力：
 * - `dict`：数据字典（读登录即可，写需 sys:config:manage）
 * - `interfaceLog`：外部接入登记（需 sys:audit:view）
 * - `wave`：波次编组与手工拆单（需 wl:delivery:manage）
 * - `inquiry`：询价比价（需 wl:purchase:manage）
 * - `budgetQuota`：预算与定额（读 cfg_budget）
 */
export function createOpsExtrasApi(http: HttpClient) {
  return {
    dict: {
      list(keyword?: string): Promise<DictRow[]> {
        return http.request<DictRow[]>({ path: '/sys/dict', query: toQuery({ keyword }) });
      },
      items(code: string): Promise<DictItemRow[]> {
        return http.request<DictItemRow[]>({ path: `/sys/dict/${encodeURIComponent(code)}` });
      },
      save(body: DictSaveRequest): Promise<{ id: string; dictCode: string; created?: boolean }> {
        return http.request({ path: '/sys/dict', method: 'POST', body });
      },
      saveItem(body: DictItemSaveRequest): Promise<{ id: string; created?: boolean }> {
        return http.request({ path: '/sys/dict/item', method: 'POST', body });
      },
      removeItem(id: Id): Promise<{ id: string; deleted: boolean }> {
        return http.request({ path: `/sys/dict/item/${id}`, method: 'DELETE' });
      }
    },

    interfaceLog: {
      list(query: InterfaceLogQuery = {}): Promise<PageResult<InterfaceLogRow>> {
        return http.request<PageResult<InterfaceLogRow>>({ path: '/sys/interface-log', query: toQuery(query) });
      }
    },

    wave: {
      list(): Promise<WaveRow[]> {
        return http.request<WaveRow[]>({ path: '/wl/wave' });
      },
      orders(query: { waveNo?: string; unassigned?: boolean } = {}): Promise<WaveOrderRow[]> {
        return http.request<WaveOrderRow[]>({ path: '/wl/wave/orders', query: toQuery(query) });
      },
      assign(body: WaveAssignRequest): Promise<{ waveNo: string; assigned: number }> {
        return http.request({ path: '/wl/wave/assign', method: 'POST', body });
      },
      /** 手工拆单：把选中明细行拆到新的 GY 子单。 */
      split(orderId: Id, body: SplitOrderRequest): Promise<SplitOrderResult> {
        return http.request<SplitOrderResult>({ path: `/wl/supply-order/${orderId}/split`, method: 'POST', body });
      }
    },

    inquiry: {
      list(query: { status?: string; size?: number; lastId?: string } = {}): Promise<PageResult<InquiryRow>> {
        return http.request<PageResult<InquiryRow>>({ path: '/wl/inquiry', query: toQuery(query) });
      },
      detail(id: Id): Promise<InquiryDetail> {
        return http.request<InquiryDetail>({ path: `/wl/inquiry/${id}` });
      },
      create(body: InquiryCreateRequest): Promise<{ id: string; bizNo: string }> {
        return http.request({ path: '/wl/inquiry', method: 'POST', body });
      },
      quote(id: Id, body: QuoteCreateRequest): Promise<{ quoteId: string; totalAmount: number }> {
        return http.request({ path: `/wl/inquiry/${id}/quote`, method: 'POST', body });
      },
      /** 定标并自动生成 CG 采购单（同一事务）。 */
      award(
        id: Id,
        body: { quoteId: string; remark?: string }
      ): Promise<{
        id: string;
        quoteId: string;
        purchaseOrderId: string;
        purchaseOrderNo: string;
        warehouseId: string;
        warehouseName: string | null;
      }> {
        return http.request({ path: `/wl/inquiry/${id}/award`, method: 'POST', body });
      },
      cancel(id: Id): Promise<{ id: string; status: string }> {
        return http.request({ path: `/wl/inquiry/${id}/cancel`, method: 'POST' });
      }
    },

    budgetQuota: {
      get(query: { budgetYear?: number } = {}): Promise<BudgetQuotaResult> {
        return http.request<BudgetQuotaResult>({ path: '/wl/budget/quota', query: toQuery(query) });
      }
    }
  };
}

export type OpsExtrasApi = ReturnType<typeof createOpsExtrasApi>;
